/* eslint-disable react/destructuring-assignment */
import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isArray, isDefined } from '@steelbuy/ts-shared';
import { buildClassStringFromClassMap } from '@steelbuy/util';

import { Icon } from '../icon/Icon';
import { IconIdentifier } from '../icon/Icon.enums';
import { InputCheckbox } from '../input-checkbox/InputCheckbox';

import './Dropdown.scss';

export type SelectOption = {
    label: string;
    value: string;
    iconName?: IconIdentifier;
};

type SingleSelectProps = {
    multiselect?: false;
    preselection?: string;
    onChange: (selectedValue?: string) => void;
};

type MultiSelectProps = {
    multiselect: true;
    preselection?: string[];
    onChange: (selectedValues: string[]) => void;
};

const SELECT_ALL = 'SELECT_ALL';

export type DropdownProps = {
    title?: string;
    options: SelectOption[];
    disabled?: boolean;
    className?: string;
    placeholder?: string;
    selectedLabelMapper?: (option: SelectOption) => string;
    fixedLabel?: string;
    selectAll?: boolean;
} & (SingleSelectProps | MultiSelectProps);

const DropdownOption = ({
    option,
    handleOnClick,
    checked,
    multiselect,
    isSelectAll = false,
}: {
    option: SelectOption;
    handleOnClick: (option: SelectOption) => void;
    checked: boolean;
    multiselect?: boolean;
    isSelectAll?: boolean;
}) => (
    <li
        className={classNames('dropdown__list__item', { 'dropdown__list__item--select-all': isSelectAll })}
        key={option.value}
    >
        <button
            type="button"
            onClick={() => {
                handleOnClick(option);
            }}
            className={classNames({ 'dropdown__list__item__button--multi': multiselect })}
        >
            {option.iconName ? <Icon name={option.iconName} /> : null}
            {multiselect ? <InputCheckbox checked={checked} onChange={() => handleOnClick(option)} /> : null}
            <span className="item__label">{option.label}</span>
            {!multiselect ? (
                <span className="item--is-selected">{checked && <Icon name={IconIdentifier.OK} />}</span>
            ) : null}
        </button>
    </li>
);

export const Dropdown = (props: DropdownProps) => {
    const {
        title,
        options,
        className,
        placeholder,
        disabled = false,
        multiselect = false,
        preselection = [],
        selectedLabelMapper,
        fixedLabel,
        selectAll,
    } = props;

    const { t } = useTranslation(['uiPrimitive']);

    const [open, setOpen] = useState(false);

    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as HTMLElement)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener('click', handleClick);
            return () => {
                document.removeEventListener('click', handleClick);
            };
        }
        return undefined;
    }, [ref, open]);

    function getPreselectedOptions() {
        const preselectedOptions = isArray(preselection) ? preselection : [preselection];
        return preselectedOptions.map((value) => options.find((option) => option.value === value)).filter(isDefined);
    }

    const selection = getPreselectedOptions();
    const toggle = () => setOpen(!open);

    const dropdownClassMap = {
        dropdown: true,
        'dropdown--multiselect': multiselect,
        'dropdown--disabled': disabled,
        [className ?? '']: true,
    };

    const isOptionInSelection = (option: SelectOption) => selection.some((selected) => selected.value === option.value);

    const isAllSelected = !!(selectAll && selection.length === options.length);

    const selectAllOption = {
        label: isAllSelected ? t('uiPrimitive:dropdown.deselectAll') : t('uiPrimitive:dropdown.selectAll'),
        value: SELECT_ALL,
    };

    const toggleSelectAll = () => {
        if (!props.multiselect) {
            return;
        }
        if (isAllSelected) {
            props.onChange([]);
            return;
        }
        props.onChange(options.map((option) => option.value) || []);
    };

    const handleOnClick = (option: SelectOption) => {
        let newSelection: SelectOption[];

        if (!isOptionInSelection(option)) {
            if (!multiselect) {
                newSelection = [option];
                setOpen(false);
            } else {
                newSelection = [...selection, option];
            }
        } else {
            newSelection = selection.filter((current) => current.value !== option.value);
        }

        // need to access via `props.` here to ensure TypeScript is getting the types right
        if (props.multiselect) {
            props.onChange(newSelection.map((e) => e.value));
        } else {
            props.onChange(newSelection[0]?.value);
        }
    };

    const renderIcon = () => {
        if (open) {
            return <Icon name={IconIdentifier.CHEVRON_UP} />;
        }
        return <Icon name={IconIdentifier.CHEVRON_DOWN} />;
    };

    const renderTitle = () => {
        if (fixedLabel) {
            return <span className="dropdown__wrapper__header__title">{fixedLabel}</span>;
        }

        if (selection.length === 0) {
            return <span className="dropdown__wrapper__header__title--empty">{placeholder}</span>;
        }

        return (
            <span className="dropdown__wrapper__header__title">
                {selection
                    .map((selectedItem) =>
                        selectedLabelMapper ? selectedLabelMapper(selectedItem) : selectedItem.label
                    )
                    .join(', ')}
            </span>
        );
    };

    function renderDropdownList() {
        return (
            <ul className="dropdown__list">
                {selectAll && (
                    <DropdownOption
                        option={selectAllOption}
                        handleOnClick={toggleSelectAll}
                        checked={isAllSelected}
                        multiselect
                        isSelectAll
                    />
                )}
                {options.map((option) => (
                    <DropdownOption
                        key={option.value}
                        option={option}
                        handleOnClick={handleOnClick}
                        checked={isOptionInSelection(option)}
                        multiselect={multiselect}
                    />
                ))}
            </ul>
        );
    }

    return (
        <div className={buildClassStringFromClassMap(dropdownClassMap)} ref={ref}>
            {title && <label className="dropdown__label">{title}</label>}
            <div className="dropdown__wrapper">
                <div
                    className="dropdown__wrapper__header"
                    tabIndex={0}
                    role="button"
                    onKeyPress={() => toggle()}
                    onClick={() => toggle()}
                >
                    {renderTitle()}
                    {renderIcon()}
                </div>
                {open ? renderDropdownList() : null}
            </div>
        </div>
    );
};
